import React from 'react'
import { Loader2 } from 'lucide-react'
import { clsx } from 'clsx'

/**
 * Loading Spinner Component
 * 
 * A reusable loading indicator with different sizes and styles.
 * Can be used inline or as an overlay.
 */
const LoadingSpinner = ({ 
  size = 'md', 
  className = '', 
  overlay = false,
  text = '',
  color = 'indigo'
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  }

  const colorClasses = {
    indigo: 'text-indigo-600',
    white: 'text-white',
    gray: 'text-gray-600',
    green: 'text-green-600',
    red: 'text-red-600'
  }

  const spinnerElement = (
    <div className={clsx('flex items-center justify-center', className)}>
      <div className="flex flex-col items-center">
        <Loader2 
          className={clsx(
            'animate-spin',
            sizeClasses[size],
            colorClasses[color]
          )} 
        />
        {text && (
          <p className={clsx(
            'mt-2 text-sm',
            colorClasses[color]
          )}>
            {text}
          </p>
        )}
      </div>
    </div>
  )

  if (overlay) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6">
          {spinnerElement}
        </div>
      </div>
    )
  }

  return spinnerElement
}

/**
 * Page Loading Spinner
 * 
 * Full-page loading indicator for route transitions
 */
export const PageLoadingSpinner = ({ message = 'Loading...' }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <LoadingSpinner 
        size="lg" 
        text={message}
        color="indigo"
      />
    </div>
  )
}

/**
 * Button Loading Spinner
 * 
 * Small spinner for button loading states
 */
export const ButtonSpinner = ({ className = '' }) => {
  return (
    <LoadingSpinner 
      size="sm" 
      color="white"
      className={className}
    />
  )
}

/**
 * Card Loading Spinner
 * 
 * Loading indicator for card content
 */
export const CardSpinner = ({ message = 'Loading...' }) => {
  return (
    <div className="flex items-center justify-center py-12">
      <LoadingSpinner 
        size="md" 
        text={message}
        color="gray"
      />
    </div>
  )
}

export default LoadingSpinner 